<script>
import { mapGetters } from 'vuex';
import { toast } from 'dashboard/composables/useToast';
import SettingsSectionCard from 'dashboard/components-next/Settings/SettingsSectionCard.vue';
import { useVuelidate } from '@vuelidate/core';
import { required, minLength } from '@vuelidate/validators';
import InputRadioGroup from './components/InputRadioGroup.vue';
import SingleSelectDropdown from './components/SingleSelectDropdown.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import EmailProviderQuickConfig from './components/EmailProviderQuickConfig.vue';
import InboxesAPI from 'dashboard/api/inboxes';

// Chaves de localStorage usadas pelo seletor "Configuracao rapida".
// _SMTP eh consumido aqui (preset deixado pelo IMAP). _IMAP eh produzido
// aqui (preset SMTP-first → consumido depois pelo ImapSettings).
const QUICK_CONFIG_SMTP_KEY = 'inbox_email_quick_config';
const QUICK_CONFIG_IMAP_KEY = 'inbox_email_quick_config_imap';

export default {
  components: {
    SettingsSectionCard,
    InputRadioGroup,
    SingleSelectDropdown,
    NextButton,
    EmailProviderQuickConfig,
  },
  props: {
    inbox: {
      type: Object,
      default: () => ({}),
    },
  },
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      isSMTPEnabled: false,
      address: '',
      port: '',
      login: '',
      password: '',
      domain: '',
      ssl: false,
      starttls: true,
      openSSLVerifyMode: 'none',
      authMechanism: 'login',
      encryptionProtocols: [
        { id: 'ssl', title: 'SSL/TLS', checked: false },
        { id: 'starttls', title: 'STARTTLS', checked: true },
      ],
      openSSLVerifyModes: [
        { key: 1, value: 'none' },
        { key: 2, value: 'peer' },
        { key: 3, value: 'client_once' },
        { key: 4, value: 'fail_if_no_peer_cert' },
      ],
      authMechanisms: [
        { key: 1, value: 'plain' },
        { key: 2, value: 'login' },
        { key: 3, value: 'cram-md5' },
        { key: 4, value: 'xoauth' },
        { key: 5, value: 'xoauth2' },
        { key: 6, value: 'ntlm' },
        { key: 7, value: 'gssapi' },
      ],
      testing: false,
      testResult: '',
      btnTestLabel: 'Testar conexão',
      btnTestingLabel: 'Testando...',
    };
  },
  validations: {
    address: { required },
    port: {
      required,
      minLength: minLength(2),
    },
    login: { required },
    password: { required },
    domain: { required },
  },
  computed: {
    ...mapGetters({ uiFlags: 'inboxes/getUIFlags' }),
  },
  watch: {
    inbox() {
      this.setDefaults();
    },
  },
  mounted() {
    this.setDefaults();
  },
  methods: {
    setDefaults() {
      const {
        smtp_enabled,
        smtp_address,
        smtp_port,
        smtp_login,
        smtp_password,
        smtp_domain,
        smtp_enable_starttls_auto,
        smtp_enable_ssl_tls,
        smtp_openssl_verify_mode,
        smtp_authentication,
      } = this.inbox;
      this.isSMTPEnabled = smtp_enabled;
      this.address = smtp_address;
      this.port = smtp_port;
      this.login = smtp_login;
      this.password = smtp_password;
      this.domain = smtp_domain;
      this.starttls = smtp_enable_starttls_auto;
      this.ssl = smtp_enable_ssl_tls;
      this.openSSLVerifyMode = smtp_openssl_verify_mode;
      this.authMechanism = smtp_authentication;

      this.encryptionProtocols = [
        { id: 'ssl', title: 'SSL/TLS', checked: smtp_enable_ssl_tls },
        {
          id: 'starttls',
          title: 'STARTTLS',
          checked: smtp_enable_starttls_auto,
        },
      ];

      // Se o agente acabou de escolher um provedor no IMAP, aplica os
      // valores SMTP automaticamente (a primeira vez). Nao sobrescreve
      // se a inbox ja tem config salva no banco.
      if (!smtp_address) {
        this.applyQuickConfigFromStorage();
      }
    },
    applyQuickConfigFromStorage() {
      try {
        const raw = localStorage.getItem(QUICK_CONFIG_SMTP_KEY);
        if (!raw) return;
        const cfg = JSON.parse(raw);
        if (cfg.address) this.address = cfg.address;
        if (cfg.port) this.port = String(cfg.port);
        if (cfg.encryption === 'starttls') {
          this.ssl = false;
          this.starttls = true;
          this.encryptionProtocols = [
            { id: 'ssl', title: 'SSL/TLS', checked: false },
            { id: 'starttls', title: 'STARTTLS', checked: true },
          ];
        } else if (cfg.encryption === 'ssl') {
          this.ssl = true;
          this.starttls = false;
          this.encryptionProtocols = [
            { id: 'ssl', title: 'SSL/TLS', checked: true },
            { id: 'starttls', title: 'STARTTLS', checked: false },
          ];
        }
        if (cfg.verifyMode) this.openSSLVerifyMode = cfg.verifyMode;
        if (cfg.auth) this.authMechanism = cfg.auth;
        // Consome o preset (so aplica uma vez).
        localStorage.removeItem(QUICK_CONFIG_SMTP_KEY);
      } catch (_e) {
        // Ignora — formato invalido ou storage cheio.
      }
    },
    applyQuickConfigFromPicker({ imap, smtp }) {
      // Aplica os valores SMTP no form atual.
      if (smtp?.address !== undefined) this.address = smtp.address;
      if (smtp?.port !== undefined) this.port = String(smtp.port);
      if (smtp?.encryption === 'starttls') {
        this.ssl = false;
        this.starttls = true;
        this.encryptionProtocols = [
          { id: 'ssl', title: 'SSL/TLS', checked: false },
          { id: 'starttls', title: 'STARTTLS', checked: true },
        ];
      } else if (smtp?.encryption === 'ssl') {
        this.ssl = true;
        this.starttls = false;
        this.encryptionProtocols = [
          { id: 'ssl', title: 'SSL/TLS', checked: true },
          { id: 'starttls', title: 'STARTTLS', checked: false },
        ];
      }
      if (smtp?.verifyMode) this.openSSLVerifyMode = smtp.verifyMode;
      if (smtp?.auth) this.authMechanism = smtp.auth;
      // Auto-preenche dominio quando login ja tem email. Geralmente
      // o dominio do SMTP eh igual ao dominio do endereco.
      if (!this.domain && this.login && this.login.includes('@')) {
        this.domain = this.login.split('@').pop();
      }
      // Guarda preset IMAP pro ImapSettings consumir quando o agente
      // for pra aba IMAP. Espelha o que ImapSettings faz com o SMTP.
      try {
        localStorage.setItem(QUICK_CONFIG_IMAP_KEY, JSON.stringify(imap));
      } catch (_e) {
        // localStorage indisponivel — agente vai digitar manualmente
      }
    },
    handleEncryptionChange(encryption) {
      if (encryption.id === 'ssl') {
        this.ssl = true;
        this.starttls = false;
      } else {
        this.ssl = false;
        this.starttls = true;
      }
    },
    handleSSLModeChange(mode) {
      this.openSSLVerifyMode = mode;
    },
    handleAuthMechanismChange(mode) {
      this.authMechanism = mode;
    },
    async testSmtp() {
      this.testing = true;
      this.testResult = '';
      try {
        const { data } = await InboxesAPI.testEmailConnection({
          kind: 'smtp',
          config: {
            smtp_address: this.address,
            smtp_port: this.port,
            smtp_login: this.login,
            smtp_password: this.password,
            smtp_domain: this.domain,
            smtp_enable_ssl_tls: this.ssl,
            smtp_enable_starttls_auto: this.starttls,
            smtp_openssl_verify_mode: this.openSSLVerifyMode,
            smtp_authentication: this.authMechanism,
          },
        });
        this.testResult = data.ok ? 'ok:Conexão OK' : `error:${data.error}`;
      } catch (e) {
        this.testResult = `error:${e?.response?.data?.error || e.message}`;
      } finally {
        this.testing = false;
      }
    },
    async updateInbox() {
      try {
        const payload = {
          id: this.inbox.id,
          channel: {
            smtp_enabled: this.isSMTPEnabled,
            smtp_address: this.address,
            smtp_port: this.port,
            smtp_login: this.login,
            smtp_password: this.password,
            smtp_domain: this.domain,
            smtp_enable_ssl_tls: this.ssl,
            smtp_enable_starttls_auto: this.starttls,
            smtp_openssl_verify_mode: this.openSSLVerifyMode,
            smtp_authentication: this.authMechanism,
          },
        };
        await this.$store.dispatch('inboxes/updateInboxSMTP', payload);
        toast.success(this.$t('INBOX_MGMT.SMTP.EDIT.SUCCESS_MESSAGE'));
      } catch (error) {
        toast.error(this.$t('INBOX_MGMT.SMTP.EDIT.ERROR_MESSAGE'));
      }
    },
  },
};
</script>

<template>
  <div class="mx-8">
    <SettingsSectionCard
      :title="$t('INBOX_MGMT.SMTP.TITLE')"
      :description="$t('INBOX_MGMT.SMTP.SUBTITLE')"
    >
      <form @submit.prevent="updateInbox">
        <label for="toggle-enable-smtp">
          <input
            v-model="isSMTPEnabled"
            type="checkbox"
            name="toggle-enable-smtp"
          />
          {{ $t('INBOX_MGMT.SMTP.TOGGLE_AVAILABILITY') }}
        </label>
        <p>{{ $t('INBOX_MGMT.SMTP.TOGGLE_HELP') }}</p>
        <div
          v-if="isSMTPEnabled"
          class="flex gap-2 items-start mt-3 mb-4 px-3 py-2 rounded-lg bg-n-amber-2 border border-n-amber-6 text-xs text-n-amber-12"
        >
          <span
            class="i-lucide-alert-triangle size-4 text-n-amber-11 flex-shrink-0 mt-0.5"
          />
          <div>{{ $t('INBOX_MGMT.SMTP.HETZNER_PORT_WARNING') }}</div>
        </div>
        <EmailProviderQuickConfig
          v-if="isSMTPEnabled"
          @apply="applyQuickConfigFromPicker"
        />
        <div v-if="isSMTPEnabled" class="mb-6">
          <woot-input
            v-model="address"
            :class="{ error: v$.address.$error }"
            class="max-w-[75%] w-full"
            :label="$t('INBOX_MGMT.SMTP.ADDRESS.LABEL')"
            :placeholder="$t('INBOX_MGMT.SMTP.ADDRESS.PLACE_HOLDER')"
            @blur="v$.address.$touch"
          />
          <woot-input
            v-model="port"
            type="number"
            :class="{ error: v$.port.$error }"
            class="max-w-[75%] w-full"
            :label="$t('INBOX_MGMT.SMTP.PORT.LABEL')"
            :placeholder="$t('INBOX_MGMT.SMTP.PORT.PLACE_HOLDER')"
            @blur="v$.port.$touch"
          />
          <woot-input
            v-model="login"
            :class="{ error: v$.login.$error }"
            class="max-w-[75%] w-full"
            :label="$t('INBOX_MGMT.SMTP.LOGIN.LABEL')"
            :placeholder="$t('INBOX_MGMT.SMTP.LOGIN.PLACE_HOLDER')"
            @blur="v$.login.$touch"
          />
          <woot-input
            v-model="password"
            :class="{ error: v$.password.$error }"
            class="max-w-[75%] w-full"
            :label="$t('INBOX_MGMT.SMTP.PASSWORD.LABEL')"
            :placeholder="$t('INBOX_MGMT.SMTP.PASSWORD.PLACE_HOLDER')"
            type="password"
            @blur="v$.password.$touch"
          />
          <woot-input
            v-model="domain"
            :class="{ error: v$.domain.$error }"
            class="max-w-[75%] w-full"
            :label="$t('INBOX_MGMT.SMTP.DOMAIN.LABEL')"
            :placeholder="$t('INBOX_MGMT.SMTP.DOMAIN.PLACE_HOLDER')"
            @blur="v$.domain.$touch"
          />
          <InputRadioGroup
            :label="$t('INBOX_MGMT.SMTP.ENCRYPTION')"
            :items="encryptionProtocols"
            :action="handleEncryptionChange"
          />
          <SingleSelectDropdown
            class="max-w-[75%] w-full"
            :label="$t('INBOX_MGMT.SMTP.OPEN_SSL_VERIFY_MODE')"
            :selected="openSSLVerifyMode"
            :options="openSSLVerifyModes"
            :action="handleSSLModeChange"
          />
          <SingleSelectDropdown
            class="max-w-[75%] w-full"
            :label="$t('INBOX_MGMT.SMTP.AUTH_MECHANISM')"
            :selected="authMechanism"
            :options="authMechanisms"
            :action="handleAuthMechanismChange"
          />
          <div class="mt-3 flex items-center gap-2">
            <button
              type="button"
              class="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-n-weak rounded-lg hover:bg-n-slate-2 disabled:opacity-50"
              :disabled="
                testing || !address || !port || !login || !password || !domain
              "
              @click="testSmtp"
            >
              <span
                :class="
                  testing
                    ? 'i-lucide-loader-2 animate-spin'
                    : 'i-lucide-plug-zap'
                "
                class="size-3.5"
              />
              {{ testing ? btnTestingLabel : btnTestLabel }}
            </button>
            <span
              v-if="testResult.startsWith('ok:')"
              class="text-xs text-n-teal-11 inline-flex items-center gap-1"
            >
              <span class="i-lucide-check-circle size-3.5" />
              {{ testResult.slice(3) }}
            </span>
            <span
              v-else-if="testResult.startsWith('error:')"
              class="text-xs text-n-ruby-11 inline-flex items-center gap-1"
            >
              <span class="i-lucide-x-circle size-3.5" />
              {{ testResult.slice(6) }}
            </span>
          </div>
        </div>
        <NextButton
          type="submit"
          :label="$t('INBOX_MGMT.SMTP.UPDATE')"
          :is-loading="uiFlags.isUpdatingSMTP"
          :disabled="(v$.$invalid && isSMTPEnabled) || uiFlags.isUpdatingSMTP"
        />
      </form>
    </SettingsSectionCard>
  </div>
</template>
